import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPhone, faShieldAlt, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import config from '../config';
import '../styles/PhoneVerificationModal.css';

const PhoneVerificationModal = ({ isOpen, onClose, user, onVerificationComplete, initialPhoneNumber }) => {
  const [step, setStep] = useState(1); // 1: phone input, 2: OTP verification, 3: completing verification
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [completingVerification, setCompletingVerification] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPhoneNumber(initialPhoneNumber || '');
      setOtpCode('');
      setError('');
      setLoading(false);
      setCompletingVerification(false);
      setResendTimer(0);
    }
  }, [isOpen, initialPhoneNumber]);

  // Country codes for phone number selection
  const countryCodes = [
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+358', country: 'Finland', flag: '🇫🇮' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+30', country: 'Greece', flag: '🇬🇷' },
    { code: '+48', country: 'Poland', flag: '🇵🇱' },
    { code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
    { code: '+36', country: 'Hungary', flag: '🇭🇺' },
    { code: '+385', country: 'Croatia', flag: '🇭🇷' },
    { code: '+386', country: 'Slovenia', flag: '🇸🇮' },
    { code: '+421', country: 'Slovakia', flag: '🇸🇰' },
    { code: '+370', country: 'Lithuania', flag: '🇱🇹' },
    { code: '+371', country: 'Latvia', flag: '🇱🇻' },
    { code: '+372', country: 'Estonia', flag: '🇪🇪' },
    { code: '+353', country: 'Ireland', flag: '🇮🇪' },
    { code: '+354', country: 'Iceland', flag: '🇮🇸' },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { code: '+378', country: 'San Marino', flag: '🇸🇲' },
    { code: '+379', country: 'Vatican City', flag: '🇻🇦' },
    { code: '+380', country: 'Ukraine', flag: '🇺🇦' },
    { code: '+7', country: 'Russia', flag: '🇷🇺' },
    { code: '+375', country: 'Belarus', flag: '🇧🇾' },
    { code: '+373', country: 'Moldova', flag: '🇲🇩' },
    { code: '+40', country: 'Romania', flag: '🇷🇴' },
    { code: '+359', country: 'Bulgaria', flag: '🇧🇬' },
    { code: '+381', country: 'Serbia', flag: '🇷🇸' },
    { code: '+382', country: 'Montenegro', flag: '🇲🇪' },
    { code: '+383', country: 'Kosovo', flag: '🇽🇰' },
    { code: '+387', country: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: '+389', country: 'North Macedonia', flag: '🇲🇰' },
    { code: '+355', country: 'Albania', flag: '🇦🇱' },
    { code: '+972', country: 'Israel', flag: '🇮🇱' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' }
  ];

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendSMS = async () => {
    setLoading(true);
    setError('');
    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber.replace(/[\s\-\(\)]/g, '')}`;
      
      // Additional validation and fallback for user ID
      if (!user || !user.id) {
        console.error('PhoneVerificationModal - No valid user or user ID found');
        setError('User session invalid. Please try logging in again.');
        return;
      }
      
      const response = await fetch(`${config.API_URL}/auth/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: fullPhoneNumber,
          userId: user.id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStep(2);
        setResendTimer(60); // 60 second cooldown
      } else {
        setError(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('PhoneVerificationModal - Fetch error:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError('');

    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber.replace(/[\s\-\(\)]/g, '')}`;
      
      const response = await fetch(`${config.API_URL}/auth/verify-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: fullPhoneNumber,
          token: otpCode,
          userId: user.id
        })
      });

      const data = await response.json();

      if (data.success) {
        // Create the verified user object
        const verifiedUser = {
          ...user,
          phone: fullPhoneNumber,
          phone_verified: true
        };
        
        // Store the verified user data in localStorage immediately
        localStorage.setItem('user', JSON.stringify(verifiedUser));
        
        // Start the completion process
        setStep(3);
        setLoading(false);
        setCompletingVerification(true);
        
        // Phone verification is complete, proceed with calling the completion handler
        setTimeout(() => {
          setCompletingVerification(false);
          onVerificationComplete(fullPhoneNumber); // 🔧 FIX: Pass the verified phone number
        }, 2000);
        
      } else {
        setError(data.message || 'Invalid verification code');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');

    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber.replace(/[\s\-\(\)]/g, '')}`;
      
      const response = await fetch(`${config.API_URL}/auth/resend-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: fullPhoneNumber,
          userId: user.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setResendTimer(60);
      } else {
        setError(data.message || 'Failed to resend code');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
    setPhoneNumber(value);
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
    setOtpCode(value);
  };

  if (!isOpen) return null;

  return (
    <div className="phone-verification-overlay">
      <div className="phone-verification-modal">
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faShieldAlt} />
            Verify Your Phone
          </h2>
          {step !== 3 && (
            <button onClick={onClose} className="close-button">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        <div className="modal-body">
          {step === 1 ? (
            // Phone Number Input Step
            <div className="phone-input-step">
              <div className="step-info">
                <h3>Add Your Phone Number</h3>
                <p>We'll send you a verification code via SMS.</p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="phone-input-container">
                <div className="country-select-container">
                  <select 
                    value={countryCode} 
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="country-select"
                  >
                    {countryCodes.map((country, index) => (
                      <option key={`${country.code}-${index}`} value={country.code}>
                        {country.flag} {country.code} {country.country}
                      </option>
                    ))}
                  </select>
                </div>
                
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  className="phone-input"
                />
              </div>

              <div className="modal-actions">
                <button 
                  onClick={handleSendSMS}
                  disabled={!phoneNumber.trim() || loading}
                  className="send-code-button"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Sending...
                    </>
                  ) : (
                    'Send Code'
                  )}
                </button>
              </div>

              {/* SMS Consent Message */}
              <div className="sms-consent">
                <p>
                  By providing your phone number, you consent to receive marketing text messages 
                  (e.g. updates, reminders, community tips) from Yesod via autodialer. Consent is not 
                  a condition of purchase. Message & data rates may apply. Frequency varies. Reply STOP 
                  to unsubscribe or click the unsubscribe link when available. See our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                  &{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>.
                </p>
              </div>
            </div>
          ) : step === 2 ? (
            // OTP Verification Step
            <div className="otp-verification-step">
              <div className="step-info">
                <h3>Enter Verification Code</h3>
                <p>
                  We sent a 6-digit code to<br />
                  <strong>{countryCode} {phoneNumber}</strong>
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="otp-input-container">
                <input
                  type="text"
                  placeholder="000000"
                  value={otpCode}
                  onChange={handleOTPChange}
                  className="otp-input"
                  maxLength="6"
                />
              </div>

              <div className="resend-section">
                <button 
                  onClick={handleResendCode}
                  disabled={resendTimer > 0 || loading}
                  className="resend-button"
                >
                  {resendTimer > 0 ? (
                    `Resend in ${resendTimer}s`
                  ) : (
                    'Resend Code'
                  )}
                </button>
              </div>

              <div className="modal-actions">
                <button 
                  onClick={handleVerifyOTP}
                  disabled={otpCode.length !== 6 || loading}
                  className="verify-button"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>

              {/* SMS Consent Message */}
              <div className="sms-consent">
                <p>
                  By providing your phone number, you consent to receive marketing text messages 
                  (e.g. updates, reminders, community tips) from Yesod via autodialer. Consent is not 
                  a condition of purchase. Message & data rates may apply. Frequency varies. Reply STOP 
                  to unsubscribe or click the unsubscribe link when available. See our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                  &{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>.
                </p>
              </div>
            </div>
          ) : (
            // Success Step
            <div className="verification-success">
              <div className="success-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h3>Phone Verified!</h3>
              <p>Your phone number has been successfully verified.</p>
              {completingVerification && (
                <div className="completing-message">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Completing verification...
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;